import React, { useEffect, useState, useRef } from 'react';
import { Trash2, Play, Pause } from 'lucide-react';
import { playNote } from '../../utils/audioUtils';
type Point = {
  x: number;
  y: number;
};
export function MelodyDrawing() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [points, setPoints] = useState<Point[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const colors = ['#FF6B6B', '#FFB347', '#FFEB3B', '#98D8AA', '#87CEEB', '#DDA0DD'];
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw grid lines
    ctx.strokeStyle = '#E0E0E0';
    ctx.lineWidth = 1;
    for (let i = 1; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 5 * i);
      ctx.lineTo(canvas.width, canvas.height / 5 * i);
      ctx.stroke();
    }
    // Draw melody line
    if (points.length > 1) {
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (let i = 1; i < points.length; i++) {
        const gradient = ctx.createLinearGradient(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
        gradient.addColorStop(0, colors[(i - 1) % colors.length]);
        gradient.addColorStop(1, colors[i % colors.length]);
        ctx.strokeStyle = gradient;
        ctx.beginPath();
        ctx.moveTo(points[i - 1].x, points[i - 1].y);
        ctx.lineTo(points[i].x, points[i].y);
        ctx.stroke();
      }
    }
  }, [points]);
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setPoints([{
        x,
        y
      }]);
    }
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setPoints(prev => [...prev, {
        x,
        y
      }]);
    }
  };
  const handleMouseUp = () => {
    setIsDrawing(false);
  };
  const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect && e.touches[0]) {
      const x = e.touches[0].clientX - rect.left;
      const y = e.touches[0].clientY - rect.top;
      setPoints([{
        x,
        y
      }]);
    }
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect && e.touches[0]) {
      const x = e.touches[0].clientX - rect.left;
      const y = e.touches[0].clientY - rect.top;
      setPoints(prev => [...prev, {
        x,
        y
      }]);
    }
  };
  const playMelody = async () => {
    if (points.length < 2 || isPlaying) return;
    setIsPlaying(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Sample points at regular intervals
    const sampleCount = 16;
    const step = Math.floor(points.length / sampleCount);
    for (let i = 0; i < sampleCount && i * step < points.length; i++) {
      const point = points[i * step];
      // Convert y position to pitch (higher position = higher pitch)
      const normalizedY = 1 - point.y / canvas.height;
      const pitches = ['1', '2', '3', '5', '6', "1'"];
      const pitchIndex = Math.floor(normalizedY * pitches.length);
      const pitch = pitches[Math.min(pitchIndex, pitches.length - 1)];
      playNote(pitch, 0.2);
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    setIsPlaying(false);
  };
  const clearCanvas = () => {
    setPoints([]);
  };
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-87">
      {/* Drawing canvas */}
      <div className="bg-white rounded-3xl p-4 w-full mb-6 shadow-lg" data-id="element-88">
        <canvas ref={canvasRef} width={600} height={300} className="w-full h-64 border-2 border-gray-100 rounded-2xl cursor-crosshair touch-none" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp} data-id="element-89" />
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 mb-6" data-id="element-90">
        <button onClick={clearCanvas} className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-91">
          <Trash2 className="w-6 h-6 text-gray-600" data-id="element-92" />
        </button>

        <button onClick={playMelody} disabled={points.length < 2 || isPlaying} className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-105 ${isPlaying ? 'bg-gray-200' : 'bg-[#673AB7]'} disabled:opacity-50`} data-id="element-93">
          {isPlaying ? <Pause className="w-8 h-8 text-gray-700" data-id="element-94" /> : <Play className="w-8 h-8 text-white ml-1" data-id="element-95" />}
        </button>
      </div>

      {/* Instructions */}
      <div className="bg-purple-50 rounded-xl p-4 max-w-md text-center" data-id="element-96">
        <p className="text-purple-800 font-medium" data-id="element-97">
          🎨 用手指或鼠标画出旋律线条
        </p>
        <p className="text-purple-600 text-sm mt-1" data-id="element-98">
          画得越高，音调越高；画得越低，音调越低
        </p>
      </div>
    </div>;
}